import { Info, LayoutTemplate, PieChart, Newspaper, X } from "lucide-react";
import Image from "next/image";
import { memo, useContext, useState } from "react";
import AppContext from "src/components/AppContext";
import InfoModal from "./InfoModal";
import PatternInsightModal from "./patternInsightModal";
import StockFundamentalRatioModal from "./stockFundamentalRatioModal";
import CompanyNewsTab from "./CompanyNewsTab";

export type CompanyInsightVariant = "portfolio" | "dataDriven";

interface CompanyInsightTabsModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  selectedCompany: string;
  companyInfo?: Record<string, string>;
  companyInfoLoading?: boolean;
  /** portfolio: pattern tab portfolio charts; dataDriven: pattern tab data-driven charts */
  variant?: CompanyInsightVariant;
  initialTab?: string;
  /** Reduced height when nested inside another modal (e.g. AnalyzeStockModal) */
  compact?: boolean;
}

const TAB_MAX_HEIGHT: Record<string, string> = {
  overview: "max-h-[85vh]", // Company Overview
  pattern: "max-h-[85vh]", // Pattern Insights
  fundamentals: "max-h-[85vh]", // Fundamentals & Ratios
  news: "max-h-[85vh]", // News tab
};

const TAB_MAX_HEIGHT_COMPACT: Record<string, string> = {
  overview: "h-[68vh] max-h-[68vh]",
  pattern: "max-h-[68vh]",
  fundamentals: "max-h-[68vh]",
  news: "max-h-[68vh]",
};

const CompanyInsightTabsModal = ({
  show,
  setShow,
  selectedCompany,
  companyInfo,
  companyInfoLoading,
  variant = "portfolio",
  initialTab = "overview",
  compact = false,
}: CompanyInsightTabsModalProps) => {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const [activeTab, setActiveTab] = useState(initialTab);

  const TABS = [
    { id: "overview", label: "Company Overview", icon: Info },
    { id: "pattern", label: "Pattern Insights", icon: LayoutTemplate },
    { id: "fundamentals", label: "Fundamentals & Ratios", icon: PieChart },
    { id: "news", label: "News", icon: Newspaper },
  ];

  if (!show) return null;

  const heightClass = (compact ? TAB_MAX_HEIGHT_COMPACT : TAB_MAX_HEIGHT)[
    activeTab
  ];
  /** Stay inside AnalyzeStockModal (max-w-[85vw]) on ≤1600px screens */
  const widthClass = compact ? "max-w-[min(78vw,72rem)]" : "max-w-7xl";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 !mt-0 backdrop-blur-md"
      onClick={() => setShow(false)}
    >
      <div
        className={`overflow-hidden shadow-2xl z-10 bg-[#f8fafc] dark:bg-gray-900/90 rounded-xl w-full ${widthClass} flex flex-col border border-gray-200 dark:border-gray-700 transition-all duration-300 ${heightClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Unified Header */}
        <div className="relative px-3 py-2 sm:py-1 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-3 w-full sm:w-auto pr-8 sm:pr-0">
            <span className="bg-white rounded-md p-1 border border-gray-300 dark:border-gray-600 flex-shrink-0 shadow-sm">
              <Image
                src={`https://firebasestorage.googleapis.com/v0/b/abillion-analytics.appspot.com/o/${selectedCompany}.jpg?alt=media`}
                alt={selectedCompany}
                width={40}
                height={40}
                unoptimized
                className="h-8 w-auto object-contain"
              />
            </span>
            <h2 className="text-lg sm:text-xl font-bold dark:text-white truncate">
              {selectedCompany}
            </h2>
          </div>

          {/* Tabs Navigation */}
          <div className="flex w-full sm:w-auto sm:absolute sm:left-1/2 sm:-translate-x-1/2 bg-gray-100/80 dark:bg-gray-900/80 p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-inner overflow-x-auto custom-scroll">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`cursor-pointer flex items-center gap-2 px-2 py-1 sm:px-2 sm:py-1 text-sm font-semibold rounded-md transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 ${activeTab === tab.id ? "text-blue-600 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setShow(false)}
            className="absolute top-1 right-1 sm:static text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700 p-2 rounded-full transition-colors flex-shrink-0 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Content Wrapper */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden relative bg-white dark:bg-gray-900">
          {activeTab === "pattern" && (
            <PatternInsightModal
              togglePatterInsightModal={() => setShow(false)}
              selectedCompany={selectedCompany}
              insightVariant={variant}
            />
          )}

          {activeTab === "fundamentals" && (
            <StockFundamentalRatioModal
              selectedCompany={selectedCompany}
              darkMode={darkMode}
            />
          )}

          {activeTab === "overview" && (
            <InfoModal
              companyInfo={companyInfo}
              loading={companyInfoLoading || false}
              compact={compact}
            />
          )}

          {activeTab === "news" && (
            <CompanyNewsTab selectedCompany={selectedCompany} />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(CompanyInsightTabsModal);
